import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, BookOpen, Check, CircleDot, Keyboard, ShieldCheck,
} from 'lucide-react';
import { apiErrorMessage } from '../../api/abfApi';
import { StatusChip } from '../../components/StatusChip';
import { ViewportToast, type ActionFeedbackState } from '../../components/ActionFeedback';
import { onboardingVersionPath } from '../../routes';
import CollectionsSchedulesEditor, { emptyCollectionsSchedules } from '../../components/CollectionsSchedulesEditor';
import ContractSchedulesEditor, { emptyContractSchedules } from '../../components/ContractSchedulesEditor';
import { normalizeEffectiveDate, validateManualAuthority, manualAuthorityPayload } from '../ManualAuthorityEditor';
import {
  Header, Journey, Layout, Note, Readable, SmallButton, Surface, TaskButton, TaskRail, Work,
} from '../review/contractReviewStyles';
import { Button, INK } from '../review/reviewStyles';
import {
  AuthorityEditor, CovenantsEditor, IdentityEditor, RulesEditor, TermsEditor,
} from './EntrySections';
import {
  buildEntrySections, emptyEntry, entryRecordPayload, notStatedKeys,
  type EntryDraft, type EntrySectionState,
} from './entryModel';
import styled from 'styled-components';

const QUEUE = '/portfolio-management?tab=credit-loans&sub=abf';

const Guidance = styled.div`
  padding: 0.95rem 1rem;
  border: 1px solid ${INK.line};
  border-radius: 10px;
  background: ${INK.panel};
  color: ${INK.soft};
  font-size: 0.86rem;
  line-height: 1.6;

  h3 { margin: 0 0 0.4rem; color: ${INK.strong}; font-size: 0.9rem; }
  p { margin: 0; }
  p + p { margin-top: 0.6rem; }
  .where { margin-top: 0.8rem; padding-top: 0.8rem; border-top: 1px solid ${INK.line}; }
  .where strong { display: block; color: ${INK.accentSoft}; font-size: 0.74rem;
    font-weight: 750; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 0.35rem; }
`;

const NextBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  margin-top: 22px;
  padding: 0.85rem 1rem;
  border: 1px solid ${INK.line};
  border-radius: 10px;
  background: rgba(9, 18, 31, 0.96);
  backdrop-filter: blur(4px);

  .state { color: ${INK.muted}; font-size: 0.84rem; }
  .state strong { color: ${INK.strong}; }
  .actions { display: flex; align-items: center; gap: 0.6rem; }
`;

/**
 * The UI lane: onboard a facility from the terms a manager holds, with no PDF.
 *
 * Built on the contract review standard (approved 2026-09-11) so both lanes read
 * as one system: the same three-step journey, the same task rail of outstanding
 * items, the selected section's editor beside what it is for, pending editors
 * kept mounted so drafts survive switching, and an impact statement that says
 * what saving creates and what it does not.
 */
const FacilityEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<EntryDraft>(emptyEntry);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const workRef = useRef<HTMLElement>(null);

  const sections = useMemo(() => buildEntrySections(draft), [draft]);
  const outstanding = sections.filter(section => section.status === 'OPEN');
  const settled = sections.filter(section => section.status === 'DONE');
  const selected = sections.find(section => section.def.key === selectedKey)
    || outstanding[0] || sections[0];

  const authority = draft.authority;
  const setAuthority = (next: typeof authority) => setDraft({ ...draft, authority: next });
  const ready = outstanding.length === 0;

  const select = (section: EntrySectionState) => {
    setSelectedKey(section.def.key);
    if (window.matchMedia('(max-width: 1000px)').matches) workRef.current?.scrollIntoView({ block: 'start' });
  };

  const selectNext = () => {
    const pool = outstanding.length ? outstanding : sections;
    const index = pool.findIndex(section => section.def.key === selected?.def.key);
    const next = pool[(index + 1) % pool.length];
    if (next) select(next);
  };

  const save = async () => {
    // The same validator that gates the payload, so the page cannot claim a
    // section is complete on a rule the save does not apply.
    const refused = validateManualAuthority(authority);
    if (refused) {
      setRefusal(refused);
      setFeedback({ tone: 'error', message: refused });
      return;
    }
    setSaving(true); setRefusal(null);
    try {
      const payload: Record<string, unknown> = {
        ...entryRecordPayload(draft),
        authority: {
          ...manualAuthorityPayload(authority),
          // Terms the maker recorded the agreement as silent on. Without this the
          // server leaves them UNKNOWN and refuses the whole save as incomplete.
          termsNotStated: notStatedKeys(draft),
        },
      };
      const { data } = await axios.post('/api/credit/abf/reference', payload);
      const versionId = data?.facilityVersion?.id;
      setFeedback({
        tone: 'success',
        message: versionId
          ? 'Facility recorded. Opening the contract review for independent activation.'
          : 'Facility recorded and staged for independent activation.',
      });
      if (versionId) window.setTimeout(() => navigate(onboardingVersionPath(versionId)), 900);
    } catch (failure) {
      const message = apiErrorMessage(failure, 'The facility could not be saved.');
      setRefusal(message);
      setFeedback({ tone: 'error', message });
    } finally { setSaving(false); }
  };

  const effectiveDate = normalizeEffectiveDate(authority.effectiveFrom);
  const fallbackClause = authority.termSourceClause.trim();

  const editorFor = (section: EntrySectionState) => {
    const def = section.def;
    switch (def.kind) {
      case 'IDENTITY':
        return <IdentityEditor value={draft.identity}
          onChange={identity => setDraft({ ...draft, identity })} />;
      case 'AUTHORITY':
        return <AuthorityEditor value={authority} onChange={setAuthority} />;
      case 'TERMS':
        return <TermsEditor title={def.label} purpose={def.purpose} fields={def.fields || []}
          value={authority} onChange={setAuthority} fallbackClause={fallbackClause} />;
      case 'RULES':
        return <RulesEditor value={authority} onChange={setAuthority} />;
      case 'COVENANTS':
        return <CovenantsEditor value={authority} onChange={setAuthority} />;
      case 'COLLECTIONS':
        return <CollectionsSchedulesEditor
          value={authority.collections || emptyCollectionsSchedules()}
          onChange={collections => setAuthority({ ...authority, collections })}
          effectiveFrom={effectiveDate || ''}
          authorityHint={authority.authorityReference.trim() || undefined}
          idPrefix="entry-collections" />;
      default:
        return <ContractSchedulesEditor
          value={authority.schedules || emptyContractSchedules()}
          onChange={schedules => setAuthority({ ...authority, schedules })}
          lane="NEW_FACILITY" />;
    }
  };

  return <Surface>
    <Header>
      <Link className="back" to={QUEUE}><ArrowLeft size={14} /> Agreement queue</Link>
      <div className="heading">
        <div>
          <div className="eyebrow">Facility onboarding · Entered terms</div>
          <h1>{draft.identity.facilityName.trim() || 'New facility'}</h1>
          <div className="meta">
            <span><Keyboard size={14} />No document required</span>
            <span>{authority.authorityReference.trim() || 'Name the executed agreement'}</span>
            {effectiveDate && <span>Effective {effectiveDate}</span>}
          </div>
        </div>
        <div className="actions">
          <StatusChip tone={ready ? 'progress' : 'neutral'}
            label={ready ? 'Ready to submit' : `${outstanding.length} outstanding`} size="md" />
        </div>
      </div>
    </Header>

    <Journey aria-label="Facility onboarding progress">
      <li data-current={!ready} aria-current={!ready ? 'step' : undefined}>
        <span className={`step-icon ${ready ? 'done' : ''}`}>{ready ? <Check size={15} /> : <CircleDot size={15} />}</span>
        <div>
          <strong>1. Enter the agreement</strong>
          <small>{ready ? 'Every required value entered' : `${outstanding.length} outstanding · ${settled.length} complete`}</small>
        </div>
      </li>
      <li>
        <span className="step-icon"><BookOpen size={15} /></span>
        <div><strong>2. Contract review</strong><small>Opens on save, with every value beside its clause</small></div>
      </li>
      <li>
        <span className="step-icon"><ShieldCheck size={15} /></span>
        <div><strong>3. Independent activation</strong><small>A second person activates the contract</small></div>
      </li>
    </Journey>

    <Layout>
      <TaskRail aria-label="Onboarding sections">
        <div className="rail-heading">
          <h2>Outstanding <span style={{ color: '#edcc82' }}>{outstanding.length}</span></h2>
          <p>All sections · select one to enter it</p>
        </div>
        <div className="tasks">
          {sections.map(section => <TaskButton key={section.def.key} type="button"
            $active={selected?.def.key === section.def.key}
            aria-pressed={selected?.def.key === section.def.key}
            onClick={() => select(section)}>
            <div className="category">{section.def.category}</div>
            <strong>{section.def.label}</strong>
            {section.status === 'OPEN'
              ? <span className="reason"><CircleDot size={12} />{section.problem}</span>
              : <span className="owner">
                {section.status === 'DONE' ? '✓ ' : ''}{section.summary}
              </span>}
          </TaskButton>)}
        </div>
        <div className="rail-footer">
          <p>A section left empty is recorded as not entered, never as a reviewed &ldquo;not stated&rdquo;. The review page asks for it again.</p>
        </div>
      </TaskRail>

      <Work ref={workRef} style={{ scrollMarginTop: 125 }} aria-label="Selected section">
        <div className="work-heading">
          <div>
            <h2>{selected?.def.label}</h2>
            <p>{selected?.status === 'OPEN' ? selected.problem : selected?.summary}</p>
          </div>
          {sections.length > 1 && <SmallButton type="button" onClick={selectNext}>
            Next section <ArrowRight size={14} />
          </SmallButton>}
        </div>
        <div className="work-pair">
          <div>
            {/* Keep every editor mounted so moving between sections preserves drafts. */}
            {sections.map(section => <Readable key={section.def.key} hidden={selected?.def.key !== section.def.key}>
              {editorFor(section)}
            </Readable>)}
          </div>
          <div>
            <div className="source-heading"><BookOpen size={14} />Where this comes from</div>
            <Guidance>
              <h3>{selected?.def.label}</h3>
              <p>{selected?.def.purpose}</p>
              <div className="where">
                <strong>In the agreement</strong>
                <p>{selected?.def.whereFound}</p>
              </div>
            </Guidance>
            {selected?.def.kind === 'TERMS' && <Note style={{ marginTop: 12 }}>
              <strong>Each value carries its own clause</strong>
              <p>Cite the clause beside the value. Left blank it falls back to {fallbackClause || 'the clause you give on the agreement itself'}, which the checker sees as the citation.</p>
            </Note>}
          </div>
        </div>

        {/* The impact statement belongs at the moment of decision, not as a
            standing accordion: while sections are outstanding it only repeated
            the next-step bar below it. */}
        {ready && <Note style={{ marginTop: 18 }}>
          <strong>Saving records the facility and hands the contract to a second person.</strong>
          <p><strong>It creates</strong> the facility record and a contract version holding every value you entered with its clause, waiting for an independent activation.</p>
          <p><strong>It does not</strong> activate the contract, create a trade, move cash, book a position or compute a borrowing base — those follow activation. An amount drawn at closing becomes a funding proposal a checker approves separately; nothing is booked from this page.</p>
        </Note>}
        {refusal && <Note role="alert" style={{ marginTop: 18 }}>
          <strong>The save was refused</strong>
          <p>{refusal}</p>
        </Note>}

        <NextBar>
          <div className="state">
            {ready
              ? <><strong>Every required value is entered.</strong> Saving opens the contract review, where a second person activates it.</>
              : <><strong>{outstanding.length} section{outstanding.length === 1 ? '' : 's'} outstanding.</strong> {outstanding[0]?.def.label} — {outstanding[0]?.problem}</>}
          </div>
          <div className="actions">
            {!ready && <SmallButton type="button" onClick={() => outstanding[0] && select(outstanding[0])}>
              Go to it <ArrowRight size={14} />
            </SmallButton>}
            <Button type="button" $variant="primary" onClick={() => void save()} disabled={saving || !ready}>
              {saving ? 'Saving…' : 'Save & send for activation'}
            </Button>
          </div>
        </NextBar>
      </Work>
    </Layout>
    <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
  </Surface>;
};

export default FacilityEntryPage;
